import type { ApiErrorBody, CvEnvelope, MessageBlock } from '../types';

export interface ChatStartResponse {
  session_token: string;
  initial_message?: string | null;
  /** 新しい public-api はリッチブロック配列でも初期メッセージを返す。 */
  initial_messages?: MessageBlock[] | null;
}

export interface ChatMessageResponse {
  message: string;
  /** 新しい public-api はリッチブロック配列でも応答を返す。 */
  messages?: MessageBlock[];
  terminated?: boolean;
}

export class ApiError extends Error {
  status: number;
  errorCode: string;
  body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? body.error_code ?? `HTTP ${status}`);
    this.status = status;
    this.errorCode = body.error_code ?? 'internal_error';
    this.body = body;
  }
}

function getApiBase(): string {
  const cfg = window.__SUZANOH_CONFIG__;
  if (cfg?.publicApiUrl) return cfg.publicApiUrl.replace(/\/+$/, '');
  return window.location.origin;
}

async function parseErrorBody(resp: Response): Promise<ApiErrorBody> {
  try {
    const data = await resp.json();
    // FastAPI HTTPException は {detail: {...}} の形で返す
    const detail = data?.detail ?? data;
    // Pydantic のバリデーション 422 は detail が配列。error_code を抽出できないので
    // validation_error にマップする（呼び出し側が非 fatal に扱えるようにするため）。
    if (Array.isArray(detail)) {
      return { error_code: 'validation_error' };
    }
    if (detail && typeof detail === 'object') {
      return {
        error_code: detail.error_code ?? detail.error ?? 'internal_error',
        message: detail.message,
      };
    }
  } catch {
    /* ignore json parse */
  }
  return { error_code: 'internal_error' };
}

async function postJson<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
  // ...init を先に置かないと、後ろから上書きされて Content-Type が消えて
  // FastAPI が 422 を返す (Pydantic が body を辞書として parse できない)
  const { headers: extraHeaders, ...restInit } = init ?? {};
  const resp = await fetch(`${getApiBase()}${path}`, {
    ...restInit,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(extraHeaders ?? {}) },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    throw new ApiError(resp.status, await parseErrorBody(resp));
  }
  return (await resp.json()) as T;
}

function isMockMode(): boolean {
  return Boolean(window.__SUZANOH_CONFIG__?.mockApi);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function startChat(params: {
  deliveryId: string;
  visitorId: string;
}): Promise<ChatStartResponse> {
  if (isMockMode()) {
    await delay(300);
    return {
      session_token: 'mock-session-token',
      initial_message: 'こんにちは！これはモック応答です。何でも入力してみてください。',
      initial_messages: [
        {
          type: 'text',
          text: 'こんにちは！これはモック応答です。何でも入力してみてください。',
        },
      ],
    };
  }
  return postJson<ChatStartResponse>('/chat/start', {
    delivery_id: params.deliveryId,
    visitor_id: params.visitorId,
  });
}

export async function sendMessage(params: {
  sessionToken: string;
  message: string;
}): Promise<ChatMessageResponse> {
  if (isMockMode()) {
    await delay(600);
    const text = `モック応答: 「${params.message}」を受け取りました。`;
    return {
      message: text,
      messages: [{ type: 'text', text }],
    };
  }
  return postJson<ChatMessageResponse>(
    '/chat/message',
    { message: params.message },
    { headers: { Authorization: `Bearer ${params.sessionToken}` } },
  );
}

/** タップされたカードがどの UI 面か (カルーセル項目 / オーナー宣言の CV カード) */
export type CvCardKind = 'carousel' | 'cv_card';

/** `/chat/cv-events` のリクエスト body。カルーセル / CV カード共通の形 */
export interface CvEventPayload {
  /** カルーセルなら item.id、CV カードなら goal_key */
  item_id: string;
  cv: CvEnvelope | null;
  link_url: string | null;
  card_kind: CvCardKind;
}

/**
 * カード (カルーセル / CV カード) クリックの CV イベントを fire-and-forget で送出する。
 * 画面遷移 (window.open) を妨げないよう keepalive で送り、失敗は握りつぶす。
 * sendBeacon は Authorization ヘッダを付与できないため fetch を使う。
 */
export function sendCvEvent(params: { sessionToken: string; payload: CvEventPayload }): void {
  if (isMockMode()) return;
  const { payload } = params;
  try {
    void fetch(`${getApiBase()}/chat/cv-events`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${params.sessionToken}`,
      },
      body: JSON.stringify(payload),
    }).catch(() => {
      /* 計測はベストエフォート。失敗してもユーザー体験に影響させない */
    });
  } catch {
    /* fetch 自体の同期例外も同様に無視 */
  }
}

/**
 * 旧サーバー互換ヘルパー:
 *   旧サーバーは `messages` / `initial_messages` を返さず `message` / `initial_message` のみ。
 *   その場合は単一 TextBlock に包んで `MessageBlock[]` を返す。
 */
export function asBlocks(
  blocks: MessageBlock[] | null | undefined,
  fallbackText: string | null | undefined,
): MessageBlock[] {
  if (Array.isArray(blocks) && blocks.length > 0) return blocks;
  if (fallbackText) return [{ type: 'text', text: fallbackText }];
  return [];
}

// ---------------------------------------------------------------------------
// Voice (SD-211)
// ---------------------------------------------------------------------------

export interface VoiceStartResponse {
  session_token: string;
  conversation_id: string;
  /** Empty string = connect to the same host as the API base (local mode). */
  ws_url: string;
}

export async function startVoiceCall(params: {
  deliveryId: string;
  visitorId: string;
}): Promise<VoiceStartResponse> {
  return postJson<VoiceStartResponse>('/voice/start', {
    delivery_id: params.deliveryId,
    visitor_id: params.visitorId,
  });
}

/** The WS base the bridge should derive its URL from when ws_url is empty. */
export function getVoiceApiBase(): string {
  return getApiBase();
}
